import React, { useRef, useEffect, forwardRef } from 'react';

const RRTAnimationPanel = forwardRef(({ navbarId, startAnimation }, ref) => {
  const canvasRef = useRef(null);
  const initializedRef = useRef(false);
  const obstaclesRef = useRef([]);
  const startRef = useRef({ x: 100, y: 0 });
  const goalRef = useRef({ x: 0, y: 0 });

  // Initialize canvas size once
  const getPanelHeight = () => {
    const navbar = document.getElementById(navbarId);
    const navbarHeight = navbar ? navbar.offsetHeight : 0;
    return (window.innerHeight - navbarHeight) * 0.8;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = canvas.parentElement.offsetWidth;
    let height = getPanelHeight();
    canvas.width = width;
    canvas.height = height;

    // Start & Goal nodes
    const s = { x: 100, y: height / 2 };
    const g = { x: width - 100, y: height / 2 };
    startRef.current = s;
    goalRef.current = g;

    // Generate obstacles
    const rectContainsPoint = (rect, p) =>
      p.x >= rect.x && p.x <= rect.x + rect.width && p.y >= rect.y && p.y <= rect.y + rect.height;

    const generateObstacles = (count = 25) => {
      const obs = [];
      const minSize = 60;
      const maxSize = 160;
      const padding = 40;
      while (obs.length < count) {
        const w = minSize + Math.random() * (maxSize - minSize);
        const h = minSize + Math.random() * (maxSize - minSize);
        const x = padding + Math.random() * (width - w - 2 * padding);
        const y = padding + Math.random() * (height - h - 2 * padding);
        const candidate = { x, y, width: w, height: h };
        if (rectContainsPoint(candidate, s) || rectContainsPoint(candidate, g)) continue;
        obs.push(candidate);
      }
      return obs;
    };

    obstaclesRef.current = generateObstacles();

    // Draw static scene once
    const drawScene = () => {
      ctx.clearRect(0, 0, width, height);

      // Obstacles
      ctx.fillStyle = 'gray';
      obstaclesRef.current.forEach(o => ctx.fillRect(o.x, o.y, o.width, o.height));

      // Start node (square)
      ctx.fillStyle = 'green';
      ctx.fillRect(s.x - 10, s.y - 10, 20, 20);

      // Goal node (star)
      ctx.fillStyle = 'yellow';
      ctx.beginPath();
      const spikes = 5, outer = 14, inner = 6;
      let rot = Math.PI / 2 * 3;
      let x = g.x, y = g.y;
      let step = Math.PI / spikes;
      ctx.moveTo(x, y - outer);
      for (let i = 0; i < spikes; i++) {
        ctx.lineTo(x + Math.cos(rot) * outer, y + Math.sin(rot) * outer);
        rot += step;
        ctx.lineTo(x + Math.cos(rot) * inner, y + Math.sin(rot) * inner);
        rot += step;
      }
      ctx.closePath();
      ctx.fill();
    };

    drawScene();

    // Handle window resize gracefully
    const handleResize = () => {
      width = canvas.parentElement.offsetWidth;
      height = getPanelHeight();
      canvas.width = width;
      canvas.height = height;

      // Reposition start & goal
      startRef.current = { x: 100, y: height / 2 };
      goalRef.current = { x: width - 100, y: height / 2 };

      // Redraw obstacles and nodes without restarting animation
      drawScene();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navbarId]);

  // Animation effect
  useEffect(() => {
    if (!startAnimation || initializedRef.current) return;
    initializedRef.current = true;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    const nodesStart = [{ node: startRef.current, parent: null }];
    const nodesGoal = [{ node: goalRef.current, parent: null }];
    const obstacles = obstaclesRef.current;

    function distance(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
    function randomNode() { return { x: Math.random() * width, y: Math.random() * height }; }
    function nearest(nodes, p) { return nodes.reduce((a, b) => distance(a.node, p) < distance(b.node, p) ? a : b); }
    function stepToward(from, to, stepSize = 20) {
      const theta = Math.atan2(to.y - from.y, to.x - from.x);
      return { x: from.x + stepSize * Math.cos(theta), y: from.y + stepSize * Math.sin(theta) };
    }
    function collides(node) { return obstacles.some(o => node.x >= o.x && node.x <= o.x + o.width && node.y >= o.y && node.y <= o.y + o.height); }
    function isPathFree(a, b, stepSize = 5) {
      const dist = distance(a, b);
      const steps = Math.ceil(dist / stepSize);
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = a.x + t * (b.x - a.x);
        const y = a.y + t * (b.y - a.y);
        if (collides({ x, y })) return false;
      }
      return true;
    }

    function drawNode(node, type = 'circle', color = 'green', size = 1) {
      ctx.fillStyle = color;
      ctx.beginPath();
      if (type === 'circle') ctx.arc(node.x, node.y, 5 * size, 0, 2 * Math.PI);
      else if (type === 'square') ctx.fillRect(node.x - 5 * size, node.y - 5 * size, 10 * size, 10 * size);
      ctx.fill();
    }
    function drawLine(a, b, color = 'blue', widthLine = 2) {
      ctx.strokeStyle = color;
      ctx.lineWidth = widthLine;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }

    let connected = false;
    let finalPath = [];

    function animate() {
      if (connected) return;

      // Start tree
      const randStart = randomNode();
      const nearestStart = nearest(nodesStart, randStart);
      const newStart = stepToward(nearestStart.node, randStart);
      if (!collides(newStart) && isPathFree(nearestStart.node, newStart)) {
        const node = { node: newStart, parent: nearestStart };
        nodesStart.push(node);
        drawLine(nearestStart.node, newStart, 'green');
        drawNode(newStart, 'circle', 'green');
      }

      // Goal tree
      const randGoal = randomNode();
      const nearestGoal = nearest(nodesGoal, randGoal);
      const newGoal = stepToward(nearestGoal.node, randGoal);
      if (!collides(newGoal) && isPathFree(nearestGoal.node, newGoal)) {
        const node = { node: newGoal, parent: nearestGoal };
        nodesGoal.push(node);
        drawLine(nearestGoal.node, newGoal, 'red');
        drawNode(newGoal, 'circle', 'red');
      }

      // Check for connection
      for (const nStart of nodesStart) {
        for (const nGoal of nodesGoal) {
          if (distance(nStart.node, nGoal.node) < 10 && isPathFree(nStart.node, nGoal.node)) {
            let path = [];
            let t = nStart;
            while (t) { path.push(t.node); t = t.parent; }
            path.reverse();
            t = nGoal;
            while (t) { path.push(t.node); t = t.parent; }
            finalPath = path;
            for (let i = 0; i < finalPath.length - 1; i++) drawLine(finalPath[i], finalPath[i + 1], 'purple', 4);
            connected = true;
            return;
          }
        }
      }

      requestAnimationFrame(animate);
    }

    requestAnimationFrame(animate);
  }, [startAnimation]);

  return (
    <div ref={ref} className="w-full relative bg-green-100" style={{ height: `${getPanelHeight()}px` }}>
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
});

export default RRTAnimationPanel;
